import { ManufacturingModelsBulkArchiveConfirm } from "./ManufacturingModelsBulkArchiveConfirm";
import { ManufacturingModelsBulkBar } from "./ManufacturingModelsBulkBar";
import { ManufacturingModelsBulkEditModal } from "./ManufacturingModelsBulkEditModal";
import { ManufacturingModelsBulkPermanentDeleteConfirm } from "./ManufacturingModelsBulkPermanentDeleteConfirm";
import { useManufacturingModelsBulkDangerActions } from "./useManufacturingModelsBulkDangerActions";
import { useManufacturingModelsExport } from "./useManufacturingModelsExport";
import { isRecipeConfirmEligible } from "./useManufacturingModelsConfirmReview";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";

/** Bulk bar + edit modal for Models list multi-select. */
export function ManufacturingModelsBulkChrome({
  selectedCount,
  filteredCount,
  allFilteredSelected,
  selectedRecipes,
  allRecipes,
  recipeGroups,
  canEditCode,
  canArchive,
  canPermanentlyDelete,
  editOpen,
  pendingCounts,
  confirmBusy,
  onSelectAllFiltered,
  onClear,
  onOpenEdit,
  onCloseEdit,
  onApplied,
  onConfirmReview,
  onToast,
}: {
  selectedCount: number;
  filteredCount: number;
  allFilteredSelected: boolean;
  selectedRecipes: readonly MfgRecipeSummary[];
  allRecipes: readonly MfgRecipeSummary[];
  recipeGroups: ReadonlyArray<{ id: string; code: string; name: string }>;
  canEditCode: boolean;
  canArchive: boolean;
  canPermanentlyDelete: boolean;
  editOpen: boolean;
  pendingCounts: Readonly<Record<string, number>>;
  confirmBusy?: boolean;
  onSelectAllFiltered: () => void;
  onClear: () => void;
  onOpenEdit: () => void;
  onCloseEdit: () => void;
  onApplied: () => void | Promise<void>;
  onConfirmReview: (recipeIds: string[]) => void;
  onToast?: (message: string) => void;
}) {
  const exp = useManufacturingModelsExport();
  const danger = useManufacturingModelsBulkDangerActions({
    selectedRecipes,
    onApplied,
    onClear,
    onToast,
  });
  const selectedIds = selectedRecipes.map((r) => r.id);
  const confirmEligible = selectedRecipes.filter((r) =>
    isRecipeConfirmEligible(r, pendingCounts),
  );
  const busy = Boolean(exp.busy || confirmBusy || danger.busy);

  return (
    <>
      <ManufacturingModelsBulkBar
        selectedCount={selectedCount}
        filteredCount={filteredCount}
        allFilteredSelected={allFilteredSelected}
        busy={busy}
        confirmEligibleCount={confirmEligible.length}
        archiveEligibleCount={danger.activeCount}
        deleteEligibleCount={danger.inactiveCount}
        canArchive={canArchive}
        canPermanentlyDelete={canPermanentlyDelete}
        onSelectAllFiltered={onSelectAllFiltered}
        onClear={onClear}
        onEditCommon={onOpenEdit}
        onExportCsv={() => void exp.exportCsv(selectedIds)}
        onExportPdf={() => void exp.exportPdf(selectedIds)}
        onConfirmReview={() => onConfirmReview(confirmEligible.map((r) => r.id))}
        onArchiveSelected={danger.openArchive}
        onDeletePermanently={danger.openDelete}
      />
      {exp.error ? (
        <div className="text-xs text-red-600 dark:text-red-400">{exp.error}</div>
      ) : null}
      {editOpen && selectedRecipes.length > 0 ? (
        <ManufacturingModelsBulkEditModal
          recipes={selectedRecipes}
          allRecipes={allRecipes}
          recipeGroups={recipeGroups}
          canEditCode={canEditCode}
          onClose={onCloseEdit}
          onApplied={onApplied}
          onClearSelection={onClear}
        />
      ) : null}
      <ManufacturingModelsBulkArchiveConfirm
        open={danger.archiveOpen}
        count={danger.activeCount}
        busy={danger.busy}
        error={danger.error}
        onCancel={danger.cancelArchive}
        onConfirm={() => void danger.confirmArchive()}
      />
      <ManufacturingModelsBulkPermanentDeleteConfirm
        open={danger.deleteOpen}
        inactiveCount={danger.inactiveCount}
        skippedActiveCount={danger.skippedActiveCount}
        busy={danger.busy}
        error={danger.error}
        blockersSummary={null}
        onCancel={danger.cancelDelete}
        onConfirm={() => void danger.confirmDelete()}
      />
    </>
  );
}
